import ClassNames from "classnames";
import { FunctionComponent, useMemo } from "react";
import { getActiveSite } from "../selectors/sites.js";
import { useAppSelector } from "../store/hooks.js";
import { createAppSelector } from "../store/utils.js";
import styles from "../stylesheets/components/language-menu.module.css";
import { ColorScheme, Language } from "../types/index.js";
import { css } from "../utils/emotion.js";
import { getLanguageMenuItems } from "../utils/utils.js";
import LanguageMenuList from "./LanguageMenuList.js";
import LanguageMenuSelect from "./LanguageMenuSelect.js";

interface Props {
  isPreview: boolean;
  languageId: Language;
  scheme: ColorScheme | undefined;
  pageId: string;
  variant: "horizontal" | "vertical";
  className?: string;
  switchToSelectLimit?: number;
}

const LanguageMenu: FunctionComponent<Props> = ({
  isPreview,
  languageId,
  scheme,
  pageId,
  variant,
  className,
  switchToSelectLimit = 3,
}) => {
  const params = useMemo(
    () => ({ isPreview, pageId, variant }),
    [isPreview, pageId, variant],
  );
  const items = useAppSelector((state) => selectItems(state, params));

  const itemsLength = items.length;
  if (!itemsLength) return null;

  return (
    <div
      className={ClassNames(
        styles.LanguageMenu,
        styles[`LanguageMenu--${variant}`],
        className,
        css({ background: scheme?.main.background }),
        {
          [styles["LanguageMenu--show-list-on-wide"]]:
            itemsLength <= switchToSelectLimit,
        },
      )}
    >
      <LanguageMenuSelect
        isPreview={isPreview}
        items={items}
        languageId={languageId}
        className={styles.LanguageMenuSelect}
      />

      {itemsLength <= switchToSelectLimit && (
        <LanguageMenuList
          isPreview={isPreview}
          items={items}
          className={styles.LanguageMenuList}
          itemClassName={styles.LanguageMenuListItem}
        />
      )}
    </div>
  );
};

const selectItems = createAppSelector(
  [
    (state) => state.pages,
    (state) => state.sites,
    (_, props: Pick<Props, "isPreview" | "pageId" | "variant">) => props,
  ],
  (pages, sites, { isPreview, pageId, variant }) =>
    getLanguageMenuItems({
      isPreview,
      pageId,
      pages,
      siteLanguages: getActiveSite(sites).languages,
      useFullLanguageNames: variant === "vertical",
    }),
);

export default LanguageMenu;
